import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import type { StateStorage } from "zustand/middleware";
import type {
  ExperimentTypes,
  ExperimentsWithEnabled
} from "@dealer-center/core-pages-experiment-enablement-interface";
import {
  getCookie,
  removeCookie,
  setCookie
} from "@dealer-center/core-typescript-cookie";

const cookiesStorage: StateStorage = {
  getItem: async (name: string) => {
    return getCookie(name) ?? null;
  },
  setItem: async (name: string, value: string) => {
    setCookie(name, value, { expires: 365 * 10 });
  },
  removeItem: async (name: string) => {
    removeCookie(name);
  }
};

export interface ExperimentState<TExperimenId extends string> {
  experiments: readonly ExperimentsWithEnabled<TExperimenId>[];
  isExperimentEnabled: (experiment: TExperimenId) => boolean;
  toggleExperiment: (experiment: TExperimenId) => void;
}

const getEnabledList = (cookieKey: string) => {
  const cookieValue = getCookie(cookieKey);
  if (cookieValue) {
    try {
      const parsedValue = JSON.parse(cookieValue);
      if (Array.isArray(parsedValue)) {
        return parsedValue.filter(
          (item: unknown) => typeof item === "string"
        ) as string[];
      }
    } catch (e) {
      console.error("Failed to parse cookie value:", e);
    }
  }
  return [];
};

const mapExperimentsWithEnabled = <TExperimenId extends string>(
  experiments: ExperimentTypes<TExperimenId>[],
  enabledList: string[]
) =>
  experiments.map(experiment => ({
    ...experiment,
    enabled: enabledList.includes(experiment.id)
  }));

// using cookies rather than local storage so that experiments can be read by lambda
export const useExperimentPreferences = <TExperimenId extends string>(
  experiments: ExperimentTypes<TExperimenId>[],
  cookieKey: string
) =>
  create<ExperimentState<TExperimenId>>()(
    persist(
      (set, get) => ({
        isExperimentEnabled: (experiment: TExperimenId) =>
          get().experiments.find(e => e.id === experiment)?.enabled ?? false,
        experiments: mapExperimentsWithEnabled(
          experiments,
          getEnabledList(cookieKey)
        ),
        toggleExperiment: (experiment: TExperimenId) => {
          set(state => {
            const updatedExperiments = state.experiments.map(e =>
              e.id === experiment ? { ...e, enabled: !e.enabled } : e
            );
            const enabledList = updatedExperiments
              .filter(e => e.enabled)
              .map(e => e.id);
            setCookie(cookieKey, JSON.stringify(enabledList), {
              expires: 365 * 10
            });
            return { experiments: updatedExperiments };
          });
        }
      }),
      {
        storage: createJSONStorage(() => cookiesStorage),
        name: cookieKey,
        partialize: state => ({
          experiments: state.experiments.filter(e => e.enabled).map(e => e.id)
        }),
        onRehydrateStorage: () => state => {
          if (state) {
            const enabledList = state.experiments as unknown as string[];
            state.experiments = mapExperimentsWithEnabled(
              experiments,
              enabledList
            );
          }
        }
      }
    )
  );
